import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, CalendarDays } from "lucide-react";
import { api, fmtDate, readTime } from "../../lib/api";
import { Reveal } from "../Reveal";
import { Badge } from "../Pill";

const META = "inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-ink/50";

export default function EditPreview() {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api("/blogs?limit=4").then(setBlogs).catch(() => {});
  }, []);

  if (!blogs.length) return null;
  const featured = blogs.find((b) => b.featured) || blogs[0];
  const rest = blogs.filter((b) => b.id !== featured.id).slice(0, 3);

  return (
    <section data-testid="edit-preview-section" className="bg-cream py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Reveal><Badge tone="gold">The Brides of India Edit</Badge></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display uppercase text-4xl md:text-5xl lg:text-6xl text-ink leading-[0.98]">
                Featured Stories
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/edit"
              data-testid="edit-view-all-link"
              className="group inline-flex items-center gap-3 border-2 border-ink rounded-full pl-6 pr-1.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-blush transition-colors duration-300"
            >
              View All Articles
              <span className="w-8 h-8 rounded-full bg-ink text-cream flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight size={14} />
              </span>
            </Link>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7">
            <Link
              to={`/edit/${featured.slug}`}
              data-testid={`featured-article-${featured.slug}`}
              className="group block bg-blush rounded-[2rem] overflow-hidden h-full"
            >
              <div className="overflow-hidden">
                <img
                  src={featured.cover_image}
                  alt={featured.title}
                  loading="lazy"
                  className="w-full aspect-[16/10] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
              </div>
              <div className="p-7 md:p-9">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge tone="ink">{featured.category}</Badge>
                  <span className={META}><Clock size={12} /> {readTime(featured.content)} min read</span>
                  <span className={META}><CalendarDays size={12} /> {fmtDate(featured.created_at)}</span>
                </div>
                <h3 className="mt-5 font-display uppercase text-3xl md:text-[2.6rem] leading-[1.02] text-ink group-hover:text-gold transition-colors duration-300">
                  {featured.title}
                </h3>
                <p className="mt-4 text-neutral-600 leading-relaxed line-clamp-2">{featured.excerpt}</p>
                <span className="mt-7 inline-flex items-center gap-3 bg-white rounded-full pl-5 pr-1.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                  Read Now
                  <span className="w-8 h-8 rounded-full bg-ink text-cream flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                    <ArrowUpRight size={14} />
                  </span>
                </span>
              </div>
            </Link>
          </Reveal>

          <div className="lg:col-span-5 flex flex-col gap-5">
            {rest.map((b, i) => (
              <Reveal key={b.id} delay={0.08 * (i + 1)} className="flex-1">
                <Link
                  to={`/edit/${b.slug}`}
                  data-testid={`edit-list-article-${b.slug}`}
                  className="group flex gap-5 h-full bg-white rounded-[1.5rem] p-4 border border-gold/10 hover:border-gold/40 transition-colors duration-300"
                >
                  <div className="w-28 md:w-36 shrink-0 overflow-hidden rounded-[1rem]">
                    <img
                      src={b.cover_image}
                      alt={b.title}
                      loading="lazy"
                      className="w-full h-full object-cover aspect-square transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    />
                  </div>
                  <div className="flex flex-col justify-center py-1 min-w-0">
                    <div className="flex flex-wrap gap-2 items-center">
                      <Badge tone="blush">{b.category}</Badge>
                      <span className={META}><Clock size={11} /> {readTime(b.content)} min</span>
                    </div>
                    <h3 className="mt-2.5 font-display uppercase text-lg md:text-xl leading-tight text-ink group-hover:text-gold transition-colors duration-300">
                      {b.title}
                    </h3>
                    <span className="mt-3 inline-flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.2em] text-ink/60">
                      Read Now <ArrowUpRight size={13} className="text-gold transition-transform duration-300 group-hover:rotate-45" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
